import { NgModule } from '@angular/core';
import { RouterModule, Routes, ExtraOptions } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { TenantGuard } from '@guards/tenant.guard';

import { AppLayoutComponent } from '@layout/app.layout.component';
import { NewFeatureSoonComponent } from '@shared/components/new-feature-soon/new-feature-soon.component';
import { PermissionGuard } from '@guards/permision.guard';
import { PermissionType } from '@admin/modules/users/models/user.model';
import { SegmentsListResolver } from '@modules/segments/resolvers/segments.resolver';
import { OutboundCallComponent } from '@modules/calls/Components/List/outbound-call/outbound-call.component';
import { ReportsComponent } from '@modules/reports/Application/Components/reports.component';
import { CollectorListResolver, PortfolioResolver } from '@resolvers/Collectors.resolver';
import { CampaignListResolver } from '@modules/campaigns/resolvers/campaigns.resolver';
import { OperationStatusListResolver } from '@modules/operations/resolvers/operation.resolver';
import { PaymentListComponent } from '@modules/payments/Components/List/payment-list/payment-list.component';

import { AgentsIframeComponent } from './modules/agents/agents-iframe.component';

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
  scrollPositionRestoration: 'top',
  scrollOffset: [0, 0],
};

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, TenantGuard],
    component: AppLayoutComponent,
    children: [
      {
        path: '',
        data: {
          breadcrumb: 'MENU.PANEL',
        },
        loadChildren: () =>
          import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: 'customer-journey',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'MENU.CUSTOMER_JOURNEY',
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('./modules/customer-journey/customer-journey.module').then(
            (m) => m.CustomerJourneyModule,
          ),
      },
      {
        path: 'outbound-calls',
        canActivate: [PermissionGuard],
        data: { breadcrumb: 'Outbound Calls' },
        loadComponent: () =>
          import('./modules/calls/Components/List/outbound-call/outbound-call.component').then(
            (c) => c.OutboundCallComponent,
          ),
      },
      {
        path: 'payments',
        canActivate: [PermissionGuard],
        data: { breadcrumb: 'Payments' },
        resolve: {
          collectors: CollectorListResolver,
        },
        loadComponent: () =>
          import('./modules/payments/Components/List/payment-list/payment-list.component').then(
            (c) => c.PaymentListComponent,
          ),
      },
      {
        path: 'behavioral-pill',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.BEHAVIORAL_PILL.TITLE',
        },
        loadChildren: () =>
          import('./modules/behavioral-pill/behavioral-pill.module').then(
            (m) => m.BehavioralPillModule,
          ),
      },
      {
        path: 'outbound-calls',
        canActivate: [PermissionGuard],
        data: { breadcrumb: 'Outbound Calls' },
        component: OutboundCallComponent,
      },
      {
        path: 'payments',
        canActivate: [PermissionGuard],
        data: { breadcrumb: 'Payments', permissions: [PermissionType.FULL_ACCESS] },
        component: PaymentListComponent,
      },

      {
        path: 'tenants',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () => import('./modules/tenants/tenants.module').then((m) => m.TenantsModule),
      },
      {
        path: 'profile',
        canActivate: [PermissionGuard],
        data: { breadcrumb: 'Perfil', permissions: [PermissionType.USER_READ] },
        loadChildren: () =>
          import('./modules/user-profile/user-profile.module').then((m) => m.UserProfileModule),
      },
      {
        path: 'operations',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.OPERATIONS.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.OPERATION_READ],
        },
        loadChildren: () =>
          import('./modules/operations/operations.module').then((m) => m.OperationsModule),
      },
      {
        path: 'customers',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'MENU.CLIENTS',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.CUSTOMER_READ],
        },
        loadChildren: () =>
          import('./modules/customers/customers.module').then((m) => m.CustomersModule),
      },
      {
        path: 'conversations',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.CONVERSATIONS.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.CONVERSATIONS],
        },
        loadChildren: () =>
          import('./modules/conversations/chat.app.module').then((m) => m.ChatAppModule),
      },
      {
        path: 'agents',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.AGENTS.TITLE',
          permissions: [PermissionType.FULL_ACCESS],
        },
        component: AgentsIframeComponent,
      },
      {
        path: 'calendar',

        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.CALENDAR.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.CAMPAIGN_CALENDAR_QUERIES],
        },
        loadChildren: () =>
          import('./modules/calendar/calendar.app.module').then((m) => m.CalendarAppModule),
      },
      {
        path: 'reports/:type',
        canActivate: [PermissionGuard],
        data: {
          label: 'Reports',
          breadcrumb: 'Reports',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.REPORTS],
        },
        component: ReportsComponent,
        resolve: {
          collectors: CollectorListResolver,
          campaigns: CampaignListResolver,
          portfolios: PortfolioResolver,
          operationStatus: OperationStatusListResolver,
        },
      },
      {
        path: 'copy',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'MENU.TEMPLATES',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.COPY_READ],
        },
        loadChildren: () => import('./modules/copies/copies.module').then((m) => m.CopiesModule),
      },
      {
        path: 'campaigns',
        data: {
          breadcrumb: 'MENU.CAMPAIGNS',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.WORKFLOW_READ],
        },
        loadChildren: () =>
          import('./modules/campaigns/campaigns.module').then((m) => m.CampaignsModule),
      },
      {
        path: 'campaigns_new',
        resolve: { segments: SegmentsListResolver },
        data: {
          breadcrumb: 'MENU.CAMPAIGNS',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.WORKFLOW_READ],
        },
        loadComponent: () =>
          import('./modules/campaigns-new/Components/campaign-list/campaign-list.component').then(
            (c) => c.CampaignListComponent,
          ),
      },
      {
        path: 'listings',

        data: {
          breadcrumb: 'DATA.MODULES.LISTING.TITLE',
        },
        loadChildren: () =>
          import('./modules/listings/listings.module').then((m) => m.ListingsModule),
      },
      {
        path: 'instances',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.INSTANCES.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.CUSTOMER_CASE_READ],
        },
        loadChildren: () =>
          import(
            './modules/customer-case-campaings-instances/customer-case-campaings-instances.module'
          ).then((m) => m.CustomerCaseCampaingsInstancesModule),
      },
      {
        path: 'import',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.IMPORT.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.IMPORT_READ],
        },
        loadChildren: () =>
          import('./modules/import-line/import-line.module').then((m) => m.ImportLineModule),
      },
      {
        path: 'payment-agreement',
        canActivate: [PermissionGuard],
        data: {
          breadcrumb: 'DATA.MODULES.PAYMENT_AGREEMENT.TITLE',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.ADMINISTRATION],
        },

        loadChildren: () =>
          import('./modules/payment-agreement/payment-agreement.module').then(
            (m) => m.PaymentAgreementModule,
          ),
      },
      {
        path: 'segments',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS, PermissionType.SEGMENT_READ],
        },
        loadChildren: () =>
          import('./modules/segments/segments.module').then((m) => m.SegmentsModule),
      },

      //PAY 2 ADMINISTRATION PANELS
      {
        path: 'brands',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/brands/brands.module').then((m) => m.BrandsModule),
      },
      {
        path: 'users',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () => import('@admin/modules/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: 'portfolios',
        canActivate: [PermissionGuard],
        data: {},
        loadChildren: () =>
          import('@admin/modules/portfolios/portfolios.module').then((m) => m.PortfoliosModule),
      },
      {
        path: 'tax-entity',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/tax-entity/tax-entity.module').then((m) => m.TaxEntityModule),
      },
      {
        path: 'services',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/services/service.module').then((m) => m.ServiceModule),
      },
      {
        path: 'partial-payment-configs',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/partial-payment-configs/partial-payment-configs.module').then(
            (m) => m.partialPaymentConfigsModule,
          ),
      },
      {
        path: 'event-hooks',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/event-hook/event-hook.module').then((m) => m.EventHooksModule),
      },
      {
        path: 'phases',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/phases/phases.module').then((m) => m.PhasesModule),
      },
      {
        path: 'regional-configs',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/regional-configs/regional-configs.module').then(
            (m) => m.RegionalConfigsModule,
          ),
      },
      {
        path: 'payment-methods',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/payment-method/payment-method.module').then(
            (m) => m.PaymentMethodModule,
          ),
      },
      {
        path: 'collectors',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/collectors/collectors.module').then((m) => m.CollectorsModule),
      },
      {
        path: 'channel-configs',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/channel-configs/channel-configs.module').then(
            (m) => m.ChannelConfigsModule,
          ),
      },
      {
        path: 'feedback-configs',
        canActivate: [PermissionGuard],
        data: {
          permissions: [PermissionType.FULL_ACCESS],
        },
        loadChildren: () =>
          import('@admin/modules/feedback-configs/feedback-configs.module').then(
            (m) => m.FeedbackConfigsModule,
          ),
      },
      {
        path: 'automations',
        data: {
          breadcrumb: 'MENU.AUTOMATIONS',
          permissions: [PermissionType.FULL_ACCESS, PermissionType.WORKFLOW_READ],
        },
        loadComponent: () =>
          import(
            './modules/automations/Components/automatisation-list/automatisation-list.component'
          ).then((m) => m.AutomatisationListComponent),
      },

      //UTILS
      {
        path: 'new-feature/:featureName',
        component: NewFeatureSoonComponent,
      },
      {
        path: 'notfound',
        loadChildren: () => import('./core/notfound/notfound.module').then((m) => m.NotfoundModule),
      },

      { path: '**', redirectTo: '/notfound' },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
